import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import dayjs from "dayjs";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  NameCell,
  StatusPill,
  DimCell,
  NumCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { useBucketCounts } from "../common/bucketCounts";
import {
  getPharmacyPurchaseOrdersApi,
  sendPharmacyPurchaseOrderApi,
  cancelPharmacyPurchaseOrderApi,
} from "../../../services/pharmacyPurchaseOrders.services";
import { formatAmount } from "../common/money";
import { canCancelDocument, cancelFailureMessage } from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";
import { getErrorMessage } from "../../../helpers/error";
import RaisePurchaseOrderModal from "./RaisePurchaseOrderModal";


/*
 * The three states the API counts an order in - draft while it is being put
 * together, completed once it has been received against, cancelled if it was
 * called off. The band is built from these, so a bucket the server does not
 * report is a bucket that would always read zero.
 */
const BUCKETS = [
  { key: "draft", label: "Draft", color: "#EAAA5E" },
  { key: "completed", label: "Completed", color: "#12B76A" },
  { key: "cancelled", label: "Cancelled", color: "#F97066" },
];

const STATUS_TONE = {
  draft: ["#FFFAEB", "#B54708"],
  completed: ["#ECFDF3", "#027A48"],
  cancelled: ["#FEF3F2", "#B42318"],
  sent: ["#FFFAEB", "#B54708"],
  partially_received: ["#EFF4FF", "#3538CD"],
  received: ["#ECFDF3", "#027A48"],
};

/* A status the server has that this screen does not know about reads as a
   state rather than as an alarm - cancelled is the red one, and a pill that
   borrowed it would say the wrong thing. */
const NEUTRAL_TONE = ["#F2F4F7", "#475467"];

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

/*
 * An order the vendor has already delivered in full, which is finished with
 * whatever its status says - the cancel endpoint refuses it, and the order
 * screen reads it the same way off the same two figures.
 */
const isFullyReceived = (order) => {
  const items = order?.items || [];
  if (!items.length) return false;
  return items.every(
    (item) => Number(item.received_qty || 0) >= Number(item.ordered_qty || 0)
  );
};

const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [working, setWorking] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const isPharmacyAdmin = useIsPharmacyAdmin();

  /* Approving or calling an order off changes what the list and the band say
     about it, so the page is read again rather than patched in the browser. */
  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (status) queryParams.status = status;
    if (search) queryParams.search = search;
    getPharmacyPurchaseOrdersApi(new URLSearchParams(queryParams))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, status, refresh]);


  const counts = useBucketCounts({
    buckets: BUCKETS,
    response: data,
    activeKey: status,
    search,
  });

  /*
   * Approving from the row.
   *
   * A draft is an order that has been put together but not yet authorised;
   * approving it is what sends it to the vendor and puts it in the queue to
   * receive against. Nothing moves in stock either way - that waits on the
   * matching purchase entry - so the only thing worth saying before it goes
   * is what it becomes.
   */
  const askToApprove = (record) => {
    Modal.confirm({
      title: `Approve ${record.order_number || "this purchase order"}?`,
      content:
        "It is marked as sent to the vendor and waits to be received against. Stock only moves when the matching purchase entry is completed.",
      okText: "Approve",
      cancelText: "Not now",
      onOk: () => {
        setWorking(record.id);
        return sendPharmacyPurchaseOrderApi(record.id)
          .then(() => {
            message.success(
              `${record.order_number || "Order"} approved and marked as sent`
            );
            refreshPage();
          })
          .catch((err) => message.error(getErrorMessage(err)))
          .finally(() => setWorking(null));
      },
    });
  };

  /*
   * Calling an order off from the row.
   *
   * The same question the foot of the order itself asks, asked here so an
   * order raised by mistake does not have to be opened to be called off. Who
   * may cancel what - and until when - is the module's one rule, and an order
   * already delivered in full is finished with on top of it.
   */
  const askToCancel = (record) => {
    Modal.confirm({
      title: "Cancel this purchase order?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        setWorking(record.id);
        return cancelPharmacyPurchaseOrderApi(record.id)
          .then(() => {
            message.success("Order cancelled.");
            refreshPage();
          })
          .catch((err) =>
            message.error(cancelFailureMessage(err, getErrorMessage(err)))
          )
          .finally(() => setWorking(null));
      },
    });
  };

  const columns = [
    {
      title: "PO No",
      dataIndex: "order_number",
      key: "order_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    {
      title: "Vendor",
      key: "vendor_name",
      render: (_, record) => <NameCell name={record.vendor_name} />,
    },
    {
      title: "Items",
      key: "items",
      align: "right",
      render: (_, record) => <NumCell>{(record.items || []).length}</NumCell>,
    },
    {
      title: "Value",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (value) => <NumCell>{formatAmount(value)}</NumCell>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <StatusPill tone={STATUS_TONE[value] || NEUTRAL_TONE}>
          {prettyStatus(value)}
        </StatusPill>
      ),
    },
    {
      title: "Raised",
      dataIndex: "order_date",
      key: "order_date",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Expected",
      dataIndex: "expected_delivery_date",
      key: "expected_delivery_date",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 196,
      render: (_, record) => (
        <RowActions>
          {/* Only a draft is waiting on a decision; anything further along has
              already had one. */}
          {record.status === "draft" ? (
            <UiButton
              size="sm"
              disabled={working === record.id}
              onClick={() => askToApprove(record)}
            >
              Approve
            </UiButton>
          ) : null}
          <IconAction
            icon="forms"
            label="View purchase order"
            onClick={() => navigate(`/pharmacy/purchase-order/${record.id}`)}
          />
          {/* An order nobody can call off any more gets no cross - a control
              that only ever refuses is worse than one that is not there. */}
          {canCancelDocument(record, { isAdmin: isPharmacyAdmin }) &&
          !isFullyReceived(record) ? (
            <IconAction
              icon="close"
              tone="danger"
              label="Cancel purchase order"
              disabled={working === record.id}
              onClick={() => askToCancel(record)}
            />
          ) : null}
        </RowActions>
      ),
    },
  ];

  const total = Number(data?.count) || 0;

  const segments = BUCKETS.map((b) => ({ ...b, count: counts[b.key] ?? 0 }));
  /* The whole list, which the unfiltered response knows outright; until
     one has come back the segments in hand are the best there is. */
  const bandTotal =
    counts.ALL ?? segments.reduce((sum, s) => sum + s.count, 0);

  return (
    <ListScreen>
      <ListHead
        title="Purchase Orders"
        actions={
          <UiButton onClick={() => setRaiseOpen(true)}>
            <CgIcon name="plus" size={16} stroke={2.2} />
            Raise Purchase Order
          </UiButton>
        }
      />

      <DistBand
        total={bandTotal}
        label="Purchase Orders"
        segments={segments}
        activeKey={status}
        onSelect={(key) => {
          setPage(1);
          setStatus((prev) => (prev === key ? null : key));
        }}
      />

      <ListCard
        title="Purchase Orders"
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: "Search PO or vendor",
        }}
        onClear={
          search || status
            ? () => {
                setSearch("");
                setStatus(null);
                setPage(1);
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={data?.results || []}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>

    
      <RaisePurchaseOrderModal
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onStart={(prefill) =>
          navigate("/pharmacy/purchase-order/new", {
            state: prefill ? { purchaseOrderPrefill: prefill } : undefined,
          })
        }
      />
    </ListScreen>
  );
};

export default PurchaseOrderList;
